import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'

interface RouteGuardProps {
  children: React.ReactNode
  isProtected: boolean
  redirectTo: string
  guard?: (_children: React.ReactNode) => React.ReactNode
  isAuthenticated: boolean
  requireOtpVerification?: boolean
  isOtpVerified?: boolean
}

const RouteGuard: React.FC<RouteGuardProps> = React.memo(
  ({
    children,
    isProtected,
    redirectTo,
    guard,
    isAuthenticated,
    requireOtpVerification = false,
    isOtpVerified = true,
  }) => {
    const location = useLocation()

    // If we're already on the target route, don't redirect
    if (location.pathname === redirectTo) {
      return <>{guard ? guard(children) : children}</>
    }

    // Handle protected routes
    if (isProtected) {
      if (!isAuthenticated) {
        return <Navigate to="/auth/login" replace />
      }

      // Check OTP verification if required
      if (requireOtpVerification && !isOtpVerified) {
        // Don't redirect if already on OTP page to avoid infinite loop
        if (location.pathname !== '/auth/signup-otp') {
          return <Navigate to="/auth/signup-otp" replace />
        }
      }

      return <>{guard ? guard(children) : children}</>
    }

    // Handle public routes (like login)
    if (!isProtected) {
      // If authenticated and on auth route, redirect to orders
      if (isAuthenticated && location.pathname.startsWith('/auth/')) {
        // Allow OTP page when OTP is not verified
        if (location.pathname === '/auth/signup-otp' && !isOtpVerified) {
          return <>{guard ? guard(children) : children}</>
        }

        // Allow navigation to signup page when OTP is not verified (for email verification)
        if (location.pathname === '/auth/signup' && !isOtpVerified) {
          return <>{guard ? guard(children) : children}</>
        }

        // Only redirect to orders if not on signup page for email verification
        return <Navigate to="/orders" replace />
      }
      return <>{guard ? guard(children) : children}</>
    }

    // Default case: render the content
    return <>{guard ? guard(children) : children}</>
  },
  (prevProps, nextProps) => {
    return (
      prevProps.isProtected === nextProps.isProtected &&
      prevProps.isAuthenticated === nextProps.isAuthenticated &&
      prevProps.redirectTo === nextProps.redirectTo &&
      prevProps.guard === nextProps.guard &&
      prevProps.children === nextProps.children &&
      prevProps.requireOtpVerification === nextProps.requireOtpVerification &&
      prevProps.isOtpVerified === nextProps.isOtpVerified
    )
  },
)

RouteGuard.displayName = 'RouteGuard'

export default RouteGuard
